import './payment-links.scss';

import { createPaymentLink } from '@api';
import { Button, Coin, Dropdown, FormInput, Modal } from '@basqet-dashboard/common/components';
import { useUserContext } from '@basqet-dashboard/common/context';
import { useCurrencies } from '@basqet-dashboard/common/hooks';
import { formatErrorMessage } from '@basqet-dashboard/common/utils';
import { ANALYTICS_EVENTS } from '@config';
import { useAnalytics } from '@hooks';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

const defaultPaymentLink = {
  name: '',
  currency: '',
  amount: ''
};

const CreatePaymentLink = ({ closeModal, onPaymentLinkCreated }) => {
  const { track } = useAnalytics();
  const { user } = useUserContext();
  const merchantId = user?.merchantId;
  const [paymentLink, setPaymentLink] = useState(defaultPaymentLink);
  const [isLoading, setIsLoading] = useState(false);
  const [currencyError, setCurrencyError] = useState('');
  const [amountError, setAmountError] = useState('');
  const [paymentNameError, setPaymentNameError] = useState('');

  useEffect(() => {
    track(ANALYTICS_EVENTS.CREATE_LINK_VIEWED, { merchant_id: merchantId });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const isCurrency = name === 'currency';

    if (isCurrency && currencyError) {
      setCurrencyError('');
    }
    if (isCurrency) {
      track(ANALYTICS_EVENTS.NEW_PAYMENT_LINK_CURRENCY_SELECTED, { merchant_id: merchantId });
    }
    if (name === 'name' && paymentNameError) {
      setPaymentNameError('');
    }
    setPaymentLink({ ...paymentLink, [name]: isCurrency ? +value : value });
  };

  const handleNameBlur = () => {
    if (paymentLink.name?.trim()) {
      track(ANALYTICS_EVENTS.NEW_PAYMENT_LINK_NAME_ENTERED, {
        merchant_id: merchantId,
        name: paymentLink.name
      });
    }
  };

  const handleAmountBlur = () => {
    if (paymentLink.amount?.trim()) {
      track(ANALYTICS_EVENTS.NEW_PAYMENT_LINK_AMOUNT_ENTERED, {
        merchant_id: merchantId,
        amount: paymentLink.amount
      });
    }
  };

  const handleAmountChange = (e) => {
    const keyRegexPattern = /\d/;

    const keyPressed = e.nativeEvent.data;

    const { name, value: inputValue } = e.target;
    const valueString = inputValue.replaceAll(',', '');

    if (
      keyRegexPattern.test(Number(keyPressed)) ||
      (keyPressed.includes('.') && !paymentLink.amount.includes('.'))
    ) {
      const values = valueString.trim().split('.');

      const leftHandside = values[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const rightHandside = values[1];
      let value = leftHandside;

      if (values.length === 2) {
        value += `.${rightHandside}`;
      }

      if (amountError) setAmountError('');

      setPaymentLink({ ...paymentLink, [name]: value });
    } else {
      return;
    }
  };

  const { fiatCurrencies } = useCurrencies();
  const formattedCurrencies = fiatCurrencies.map((currency) => {
    const { icon_url, slug, value } = currency;

    const label = (
      <>
        <Coin slug={slug} icon_url={icon_url} /> {slug}
      </>
    );
    return { value, label, slug, icon_url };
  });

  const currentCoin = formattedCurrencies.find(
    (currency) => currency.value == paymentLink.currency
  );

  const savePaymentLink = async () => {
    // validate payment name: required, 2-100 chars, allowed characters
    const name = (paymentLink.name || '').trim();
    const namePattern = new RegExp("^[A-Za-z0-9 .,&'()/-]{2,100}$");

    if (!name) return setPaymentNameError('Payment name is required');
    if (name.length < 2 || name.length > 100)
      return setPaymentNameError('Payment name must be between 2 and 100 characters');
    if (!namePattern.test(name))
      return setPaymentNameError(
        "Payment name may only contain letters, numbers, spaces and these characters: . , & ' ( ) / -"
      );

    if (!paymentLink.currency) return setCurrencyError('Choose currency');
    if (paymentLink.amount && Number(paymentLink.amount.replaceAll(',', '')) <= 0)
      return setAmountError('Amount must be greater than zero');

    const currencySlug = fiatCurrencies.find((c) => c.value == paymentLink.currency)?.slug;

    setIsLoading(true);
    try {
      const payload = {
        ...paymentLink,
        pass_fee_to_customer: false,
        pass_settlement_fee_to_customer: false
      };

      let paymentLinkAmount = payload?.amount.replaceAll(',', '');

      payload.amount = paymentLinkAmount;
      if (!payload.amount) {
        delete payload.amount;
      }

      if (!payload.currency) {
        delete payload.currency;
      }

      const response = await createPaymentLink(payload);

      if (response?.status !== 'success' || !response?.data?.url_slug) {
        throw new Error('Failed to create payment link');
      }

      const { url_slug, name: paymentLinkName, id: paymentLinkId } = response.data;

      track(ANALYTICS_EVENTS.NEW_PAYMENT_LINK_FORM_SUBMITTED, {
        merchant_id: merchantId,
        payment_link_id: paymentLinkId,
        name: name,
        amount: paymentLink.amount || null,
        currency: currencySlug || null
      });
      const paymentLinksBaseUrl = process.env.REACT_APP_PAYMENT_LINKS_BASE_URL;
      const paymentUrl = paymentLinksBaseUrl + url_slug;
      const title = `QR Code - ${paymentLinkName || 'Payment Link'}`;

      onPaymentLinkCreated(paymentUrl, title, url_slug);
    } catch (error) {
      formatErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal onModalClose={closeModal} header={{ name: 'Create new', cursive: 'payment link' }}>
      <div className="payment_links__form">
        <FormInput
          placeholder="Enter Name"
          label="Payment Link name"
          name="name"
          type="text"
          register={{ onChange: handleChange, onBlur: handleNameBlur }}
          error={paymentNameError}
        />
        <div className={'form__item'}>
          <label htmlFor="currency_id" className="form__label">
            Amount
          </label>
          <div className={`amount_currency  ${currentCoin ? 'pad__currency__left' : ''}`}>
            {currentCoin && <Coin slug={currentCoin?.slug} />}
            <Dropdown
              options={formattedCurrencies}
              name="currency"
              placeholder="Currency"
              register={{ onChange: handleChange }}
              error={currencyError}
            />
            <FormInput
              placeholder="Enter Amount"
              name="amount"
              type="text"
              register={{ onChange: handleAmountChange, onBlur: handleAmountBlur }}
              value={paymentLink?.amount}
              error={amountError}
            />
          </div>
        </div>

        <Button isFull onClick={savePaymentLink} isLoading={isLoading}>
          Create Link
        </Button>
      </div>
    </Modal>
  );
};

CreatePaymentLink.propTypes = {
  closeModal: PropTypes.func,
  onPaymentLinkCreated: PropTypes.func
};
export { CreatePaymentLink };
